import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  forwardRef,
  NgZone,
  OnInit
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { Router } from '@angular/router';
import { ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';

import { SelectInstantBaseNewComponent } from '@shared-feature/components/select/selectInstant-base-new';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectComponent, NzSelectModule } from 'ng-zorro-antd/select';
import { CurrencyModalComponent } from 'src/app/components/modal/currency-modal/currency-modal.component';
import { ViewItemComponent } from 'src/app/shared/components/view-item/view-item.component';

@Component({
  selector: 'app-currency-select',
  templateUrl: './currency-select.component.html',
  standalone: true,
  styles: [
    `
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      .currency-select-container {
        position: relative;
        width: 100%;
      }

      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }


      ::ng-deep .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(.ant-select-item-option-active):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
      ::ng-deep .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(.ant-select-item-option-active):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }

    

    `
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzInputModule,
    NzIconModule,
    ViewItemComponent,
    CurrencyModalComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencySelectComponent),
      multi: true
    }
  ]
})
export class CurrencySelectComponent extends SelectInstantBaseNewComponent
implements AfterViewInit, OnInit {
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;
  @Input() option: 'both' | 'local' | 'foreign' = 'both';
  @Input() tabindex:string | null = null;

  @ViewChild(CurrencyModalComponent) modal!: CurrencyModalComponent;
  private allowMultiCurrency = true;
  constructor(
    http: _HttpClient,
    acl: ACLService,
    cdr: ChangeDetectorRef,
    router: Router,
    private ngZone: NgZone
  ) {
    super(http, cdr, router);
    this.allowMultiCurrency = acl.canAbility('m1.128');
  }

  ngOnInit(): void {
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });  
  }

 
  ngAfterViewInit(): void {
    if (this.tabindex) {
      const nzSelectElement = this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');
      }
    }
    
  }

  getUrl(searchValue: string): string {
    let filter = ``;

    if (this.option === 'local') {
      filter = '(local eq true)';
    } else if (this.option === 'foreign') {
      filter = '(local eq false)';
    }

    if (searchValue) {
      const searchStr = searchValue.replaceAll("'", "''");
      if (filter) {
        filter += ` and (contains(code, '${encodeURIComponent(searchStr)}'))`;
      } else {
        filter = `(contains(code, '${encodeURIComponent(searchStr)}'))`;
      }
    }

    let url = `api/currencies/lookup?$top=50&$skip=0&$orderby=code asc`;
    if (filter) url += `&$filter=${filter}`;

    return url;
  }

  override get readonly(): boolean {
    if (this.disabled || !this.allowEdit || !this.allowMultiCurrency) {
      return true;
    }
    return false;
  }

  // set the location want to navigate
  jump(): string {
    return '/company/currency';
  }

  // set the param
  queryParam(): String {
    return 'CurrencyName';
  }

  /**
   * Opens the currency modal with proper timing to ensure immediate display.
   * This method handles both click and keyboard events.
   */
  openCurrencyModal(event?: Event): void {
    // Prevent default behavior to avoid immediate focus changes
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Close the dropdown immediately
    if (this.selector) {
      this.selector.nzOpen = false;
    }
    
    // Capture the current search value for pre-filling the modal
    this.captureCurrentSearchValue();
    const currencyName = this.storedInstantData || this.instantData;
    
    // Force change detection to ensure dropdown closes
    this.cdr.detectChanges();
    
    // Fetch new currency data and open modal
    this.http.get('api/currencies/new').subscribe(res => {
      if (currencyName) res.name = currencyName;
      
      // Manually set visible to true first to ensure immediate UI update
      this.modal.visible = true;
      
      this.modal.show(res, (res: any) => {
        // Add the new currency to the list if it's not already there
        if (res && !this.list.find(item => item.id === res.id)) {
          this.list.unshift(res);
        }
        // Set the new currency as selected
        this.selected = res;
        this.value = res;
        // Notify parent component of the change
        this.onChange(res);
        this.selectedChange.emit(res);
        // Trigger change detection
        this.cdr.detectChanges();
        // Clear stored data after use
        this.storedInstantData = '';
      });
      
      // Force change detection to ensure UI updates immediately
      this.ngZone.run(() => {
        requestAnimationFrame(() => {
          // Force change detection on the modal component
          (this.modal as any).cdr?.detectChanges();
          // Also force change detection on the current component
          this.cdr.detectChanges();
        });
      });
    });
  }

  /**
   * Handles the "Add New" button click by redirecting to our working method.
   */
  override onAddNewClick(event?: Event): void {
    this.openCurrencyModal(event);
  }

  /**
   * Handles keyboard events, specifically Enter key for opening the modal.
   * Falls back to base class behavior for other keys.
   */
  override handleKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      // Let base class handle other keys
      super.handleKeyDown(event);
      return;
    }

    // Prevent default enter submitting parent forms
    event.preventDefault();
    event.stopPropagation();

    // Build visible options: skip the Add New pseudo option
    const options = (this.list || []).filter(x => x != null);
    const hasItems = options.length > 0;
    const keyword = (this.getCurrentSearchValue() || '').trim();

    // Check if the focused option is the "Add New" button
    const focusedOptionIndex = this.getFocusedOption();
    
    if (focusedOptionIndex === -1) {
      // Focus is on "Add New" button
      this.openCurrencyModal(event);
      return;
    }

    if (hasItems) {
      // Check if there's a focused item in the dropdown
      let selectedItem;
      
      if (focusedOptionIndex >= 0 && focusedOptionIndex < options.length) {
        // Use the focused item
        selectedItem = options[focusedOptionIndex];
      } else {
        // Fallback to first item if no focused item
        selectedItem = options[0];
      }
      
      const val = this.getValue(selectedItem);
      this.valueChanged(val);
      // Close dropdown
      try { this.selector?.setOpenState(false); } catch {}
    } else {
      // No items; if keyword present, treat as Add New
      if (keyword && this.showAddNewButton) {
        this.openCurrencyModal(event);
      } else {
        try { this.selector?.setOpenState(false); } catch {}
      }
    }
  }

 
}
